import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { withRouter } from 'react-router'
import { useDispatch } from 'react-redux'

import { propOr } from 'ramda'
import { useSelector, useMakeSelector } from 'hooks/redux'

import { HISTORY } from 'constants/featureSnapshots'
import {
  FEATURE_TYPE,
  FUNCTION_TYPE,
  COMPLEX,
  TIMER,
  OUTPUT_BLOCK_TYPE,
  SIMPLE,
  CAN_INPUT,
  FEATURE_NOTES,
} from 'utils/constants'
import {
  DEFAULT_DRAWER_WIDTH,
  HARNESS_DRAWER_WIDTH,
} from 'utils/drawerConstants'
import strings from 'utils/strings'

import {
  getIsSimulating,
  getSelectedId,
  makeComponentMetaSelector,
} from 'selectors/navigator'
import { getDrawerVisibleWidth, getShowDrawerMask } from 'selectors/drawer'
import { getShowHarnesses } from 'selectors/feature/harnesses'
import { setDrawerWidth, setDrawerVisibleWidth } from 'actions/drawer'

import {
  FeatureHistoryTopNav,
  FeatureHistoryListNavPane,
} from 'components/feature/FeatureHistory'

import { FeatureEditor } from 'components/feature'
import FunctionPane from 'components/feature/NavigatorPanes/FunctionPane'
import LogicSetEditor from 'components/feature/LogicSetEditor'
import TimerBoxEditor from 'components/feature/TimerBoxEditor'
import ConditionPane from 'components/feature/NavigatorPanes/ConditionPane'
import OutputNodePane from 'components/feature/NavigatorPanes/OutputNodePane'
import Harnesses from 'components/feature/Harnesses'
import FeatureNotes from 'components/feature/NavigatorPanes/FeatureNotes/FeatureNotes'

import LogicSetSimulator from 'components/feature/LogicSetSimulator'
import TimerBoxSimulator from 'components/feature/TimerBoxSimulator'
import SingleConditionSimulator from 'components/feature/SingleConditionSimulator'
import OutputSimulator from 'components/feature/Simulation/OutputSimulator'
import withStyle, {
  StyledReduxNavigator,
  HarnessButton,
} from './FeatureNavigator.style'

const renderStrategy = {
  [HISTORY]: FeatureHistoryListNavPane,
  [FEATURE_TYPE]: FeatureEditor,
  [FUNCTION_TYPE]: FunctionPane,
  // logic nodes
  [COMPLEX]: LogicSetEditor,
  [TIMER]: TimerBoxEditor,
  // conditions
  [SIMPLE]: ConditionPane,
  [CAN_INPUT]: ConditionPane,
  // output
  [OUTPUT_BLOCK_TYPE]: OutputNodePane,
  [FEATURE_NOTES]: FeatureNotes,
}

const renderSimulatorStrategy = {
  ...renderStrategy,
  [COMPLEX]: LogicSetSimulator,
  [SIMPLE]: SingleConditionSimulator,
  [CAN_INPUT]: SingleConditionSimulator,
  [TIMER]: TimerBoxSimulator,
  [OUTPUT_BLOCK_TYPE]: OutputSimulator,
}

const FeatureNavigator = ({ location, className }) => {
  const dispatch = useDispatch()
  const drawerVisibleWidth = useSelector(getDrawerVisibleWidth)
  const maskShowing = useSelector(getShowDrawerMask)
  const showHarnesses = useSelector(getShowHarnesses)
  const showHarnessButton = !maskShowing && showHarnesses

  const handleShowHarnesses = () => {
    const showHarnessPane = drawerVisibleWidth !== HARNESS_DRAWER_WIDTH
    if (showHarnessPane) {
      dispatch(setDrawerVisibleWidth(HARNESS_DRAWER_WIDTH))
      dispatch(setDrawerWidth(HARNESS_DRAWER_WIDTH))
    } else {
      dispatch(setDrawerWidth(DEFAULT_DRAWER_WIDTH))
      dispatch(setDrawerVisibleWidth(DEFAULT_DRAWER_WIDTH))
    }
  }

  const isSimulating = useSelector(getIsSimulating)
  const selectedId = useSelector(getSelectedId)
  const componentMeta = useMakeSelector(makeComponentMetaSelector, {
    id: selectedId,
  })
  const type = propOr(null, 'type')(componentMeta)
  const Component = useMemo(() => {
    if (isSimulating) {
      return renderSimulatorStrategy[type]
    }
    return renderStrategy[type]
  }, [type, isSimulating])

  if (!Component) return null

  const harnessBtnProps = {
    link: true,
    rotateIcon: drawerVisibleWidth === DEFAULT_DRAWER_WIDTH,
    iconProps: {
      size: 10,
      icon: 'icons-expand-collapse-double-chevron',
      fill: '#0051c4',
    },
    onClick: handleShowHarnesses,
    text: strings.harness,
  }

  const renderHarnessBtnInStatus = showHarnessButton && type === FEATURE_TYPE
  const renderHarnessBtnInTopNav = showHarnessButton && type !== FEATURE_TYPE

  const extraPropsToComponent = {}

  if (renderHarnessBtnInStatus) {
    extraPropsToComponent.renderHarnessBtn = () => (
      <StyledHarnessButton {...harnessBtnProps} relative />
    )
  }

  return (
    <div className={className}>
      <FeatureHistoryTopNav location={location}>
        {renderHarnessBtnInTopNav && (
          <StyledHarnessButton {...harnessBtnProps} />
        )}
        <StyledReduxNavigator
          render={(props) => (
            <Component
              isSimulating={isSimulating}
              selectedId={selectedId}
              type={type}
              {...props}
              {...extraPropsToComponent}
            />
          )}
        />
        {!isSimulating &&
          showHarnesses &&
          drawerVisibleWidth === HARNESS_DRAWER_WIDTH && <Harnesses />}
      </FeatureHistoryTopNav>
    </div>
  )
}

FeatureNavigator.propTypes = {
  location: PropTypes.object.isRequired,
  className: PropTypes.string,
}

export default withRouter(withStyle(FeatureNavigator))

const StyledHarnessButton = styled(HarnessButton)`
  z-index: 2;
`
